/*
 * Copyright © 2017-2026 Knowledge for Decisions (K4D). All rights reserved.
 *
 * This code and associated materials are protected by copyright laws and
 * international treaties. Unauthorized use, reproduction, distribution, or
 * modification of any content from Knowledge for Decisions (K4D) is strictly
 * prohibited. For inquiries regarding the use or licensing of K4D's
 * copyrighted materials, please contact us at contact@k4d.com.br. "Knowledge
 * for Decisions (K4D)" and the slogan "Using data to make precise decisions"
 * are registered trademarks of Knowledge for Decisions (K4D).
 */

import { PortalSettings } from './constants';

/**
 * Normalizes a raw portal-settings API payload into a well-typed object.
 *
 * @param raw - The raw API payload.
 * @param agencyId - Fallback agency id when absent from the payload.
 * @param prev - Optional previous settings to fall back to for arrays.
 * @returns The normalized portal settings.
 */
export function normalizePortalSettings(raw: any, agencyId: string, prev?: PortalSettings | null): PortalSettings {
  return {
    agency_id: raw?.agency_id ?? agencyId,
    connected_portals: Array.isArray(raw?.connected_portals)
      ? raw.connected_portals
      : (prev?.connected_portals ?? []),
    enabled_portals: Array.isArray(raw?.enabled_portals)
      ? raw.enabled_portals
      : (prev?.enabled_portals ?? []),
    auto_publish_active: Boolean(raw?.auto_publish_active),
    auto_publish_portals: Array.isArray(raw?.auto_publish_portals)
      ? raw.auto_publish_portals
      : (prev?.auto_publish_portals ?? []),
    olx_connected: Boolean(raw?.olx_connected),
    olx_user_id: raw?.olx_user_id,
    olx_user_name: raw?.olx_user_name ?? null,
    olx_user_email: raw?.olx_user_email ?? null,
    olx_token_status: raw?.olx_token_status ?? null,
    olx_account_confirmed: raw?.olx_account_confirmed ?? null,
    olx_terms_version: raw?.olx_terms_version ?? null,
    olx_plan_status: raw?.olx_plan_status ?? null,
    olx_plan_name: raw?.olx_plan_name ?? null,
    olx_plan_ads_available: raw?.olx_plan_ads_available ?? null,
    olx_plan_ads_total: raw?.olx_plan_ads_total ?? null,
    olx_plan_renews_at: raw?.olx_plan_renews_at ?? null,
    olx_block_reason: raw?.olx_block_reason ?? null,
    canal_pro_lead_url_created_at: raw?.canal_pro_lead_url_created_at ?? null,
    last_vrsync_read_at: raw?.last_vrsync_read_at,
    last_cnm_read_at: raw?.last_cnm_read_at,
    cnm_feed_url_created_at: raw?.cnm_feed_url_created_at ?? null,
    cnm_listed_ids: Array.isArray(raw?.cnm_listed_ids) ? raw.cnm_listed_ids : [],
    cnm_lead_email: raw?.cnm_lead_email ?? null,
    cnm_lead_saved_at: raw?.cnm_lead_saved_at ?? null,
  };
}
